import type { Notification } from '@bambi/api-client/schemas';
import { Button } from '@bambi/component-library/src/components/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { upperFirst } from 'lodash-es';
import { type MouseEventHandler, useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { NotificationMetaDataCallback } from './types';
import { useVehiclesInspectionsRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';

export function VehicleFailedInspectionRenderer({
  notification,
}: {
  notification: Notification;
}) {
  const { data: report, isLoading } = useVehiclesInspectionsRetrieveQuery(
    { id: notification.ref_entity_id ?? '' },
    { skip: !notification.ref_entity_id }
  );

  const navigate = useNavigate();

  const handleOpenInspectionsPage = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigate('/fleet/inspections');
    },
    [navigate]
  );

  if (isLoading) {
    return <LoadingIndicator variant="small" />;
  }

  if (!report) {
    return <div className="text-sm">Inspection report not found</div>;
  }

  const failedItems = report.report_json.filter(
    (item) => item.value === 'fail'
  );

  return (
    <div className="flex flex-col text-sm">
      <div>
        <strong>Vehicle: </strong>
        {report.vehicle.nickname}
      </div>
      <div>
        <strong>Failed items: </strong>
        {failedItems
          .map((item) => item.title?.split('_').map(upperFirst).join(' '))
          .join(', ')}
      </div>
      <Button
        variant="anchor-inline"
        justifyContent="start"
        onClick={handleOpenInspectionsPage}
      >
        View on Fleet &gt; Inspections
      </Button>
    </div>
  );
}

export const getMetaData: NotificationMetaDataCallback = (_notification) => {
  return {
    title: 'Vehicle Failed Inspection',
  };
};
